<template>
  <div class="d-flex justify-center fill-height pt-6 px-10">
    <div class="admin-content">
      <v-alert
        v-if="error"
        type="error"
        dismissible
        @input="error = null"
      >
        {{ error }}
      </v-alert>

      <v-alert
        v-if="successMessage"
        type="success"
        dismissible
        @input="successMessage = null"
      >
        {{ successMessage }}
      </v-alert>

      <div class="d-flex justify-end mb-4">
        <action-button
          color="primary"
          @click="showInviteDialog = true"
        >
          <v-icon left>person_add</v-icon>
          Invite User
        </action-button>
      </div>

      <v-tabs v-model="activeTab" class="mb-4">
        <v-tab>Users</v-tab>
        <v-tab>
          Pending Invitations
          <v-chip v-if="invites.length > 0" small class="ml-2">
            {{ invites.length }}
          </v-chip>
        </v-tab>
      </v-tabs>

      <div v-if="loading" class="d-flex justify-center py-12">
        <v-progress-circular
          indeterminate
          color="primary"
          size="64"
        />
      </div>

      <v-tabs-items v-else v-model="activeTab">
        <v-tab-item>
          <user-list
            :users="users"
            :action-in-progress="actionLoading"
            @edit="onEditUser"
            @deactivate="onDeactivateUser"
            @reactivate="onReactivateUser"
            @delete="onDeleteUser"
            @resend-password="onResendPasswordEmail"
          />
        </v-tab-item>

        <v-tab-item>
          <user-invite-list
            :invites="invites"
            :action-in-progress="actionLoading"
            @delete="onDeleteInvite"
          />
        </v-tab-item>
      </v-tabs-items>

      <!-- Invite Dialog -->
      <user-invite-dialog
        v-model="showInviteDialog"
        :loading="inviteLoading"
        @invite="onCreateInvite"
        @cancel="showInviteDialog = false"
      />

      <!-- Edit User Dialog -->
      <user-edit-dialog
        v-model="showEditDialog"
        :user="selectedUser"
        :loading="editLoading"
        @save="onSaveUser"
        @cancel="showEditDialog = false"
      />

      <!-- Confirm Deactivate Dialog -->
      <confirmation-dialog
        v-model="showDeactivateDialog"
        action-title="Deactivate"
        action-color="warning"
        action-icon="block"
        :name="selectedUser ? selectedUser.name : ''"
        :disabled="actionLoading"
        @confirm="confirmDeactivate"
      >
        They will no longer be able to access the platform.
      </confirmation-dialog>

      <!-- Confirm Delete Dialog -->
      <confirmation-dialog
        v-model="showDeleteDialog"
        action-title="Delete"
        action-color="error"
        action-icon="delete_forever"
        :name="selectedUser ? selectedUser.name : ''"
        :disabled="actionLoading"
        @confirm="confirmDelete"
      >
        <v-alert type="warning" dense text class="mb-4">
          This action cannot be undone!
        </v-alert>
        This will also remove their account from the authentication
        system.
      </confirmation-dialog>

      <!-- Confirm Revoke Invitation Dialog -->
      <confirmation-dialog
        v-model="showRevokeDialog"
        action-title="Revoke"
        action-color="error"
        action-icon="delete"
        :name="
          selectedInvite
            ? selectedInvite.name || selectedInvite.emailAddress
            : ''
        "
        :disabled="actionLoading"
        @confirm="confirmRevoke"
      >
        They will no longer be able to use their invite link.
      </confirmation-dialog>
    </div>
  </div>
</template>

<script>
import ActionButton from '@/components/Common/ActionButton';
import UserInviteDialog from '@/components/AdminUserManagementPage/UserInviteDialog';
import UserInviteList from '@/components/AdminUserManagementPage/UserInviteList';
import UserList from '@/components/AdminUserManagementPage/UserList';
import UserEditDialog from '@/components/AdminUserManagementPage/UserEditDialog';
import ConfirmationDialog from '@/components/ReviewsPage/ConfirmationDialog';
import { mapActions } from 'vuex';

export default {
  name: 'AdminUserManagementContent',
  components: {
    ActionButton,
    UserInviteDialog,
    UserInviteList,
    UserList,
    UserEditDialog,
    ConfirmationDialog,
  },
  data: () => ({
    activeTab: 0,
    users: [],
    invites: [],
    loading: false,
    inviteLoading: false,
    editLoading: false,
    actionLoading: false,
    showInviteDialog: false,
    showEditDialog: false,
    showDeactivateDialog: false,
    showDeleteDialog: false,
    showRevokeDialog: false,
    selectedUser: null,
    selectedInvite: null,
    error: null,
    successMessage: null,
  }),
  async mounted() {
    await this.loadData();
  },
  methods: {
    ...mapActions({
      fetchUsers: 'user/fetchUsers',
      fetchUserInvites: 'user/fetchUserInvites',
      createUserInvite: 'user/createUserInvite',
      deleteUserInvite: 'user/deleteUserInvite',
      updateUser: 'user/updateUser',
      deactivateUser: 'user/deactivateUser',
      reactivateUser: 'user/reactivateUser',
      deleteUser: 'user/deleteUser',
      resendPasswordSetupEmail: 'user/resendPasswordSetupEmail',
    }),

    async loadData() {
      try {
        this.loading = true;
        this.error = null;
        const [
          usersResult,
          invitesResult,
        ] = await Promise.allSettled([
          this.fetchUsers(),
          this.fetchUserInvites(),
        ]);

        if (usersResult.status === 'fulfilled') {
          this.users = usersResult.value;
        } else {
          this.error =
            'Failed to load users. Other data may still be available.';
        }

        if (invitesResult.status === 'fulfilled') {
          this.invites = invitesResult.value;
        } else {
          this.error =
            this.error ||
            'Failed to load invitations. Other data may still be available.';
        }
      } finally {
        this.loading = false;
      }
    },

    // Refetch only the list(s) a mutation can actually change, so a single
    // user/invite change doesn't re-download every user AND every invite.
    // A reload failure surfaces as a non-fatal error (mirroring loadData)
    // rather than throwing, so a successful mutation is never reported failed.
    async reload(which) {
      if (which === 'users' || which === 'both') {
        try {
          this.users = await this.fetchUsers();
        } catch (e) {
          this.error =
            this.error ||
            'Failed to reload users. Other data may still be available.';
        }
      }
      if (which === 'invites' || which === 'both') {
        try {
          this.invites = await this.fetchUserInvites();
        } catch (e) {
          this.error =
            this.error ||
            'Failed to reload invitations. Other data may still be available.';
        }
      }
    },

    // Sets successMessage only if the action/reload did not itself set an
    // error, so we never show a contradictory success + error pair at once.
    setSuccessIfLoaded(message) {
      if (!this.error) {
        this.successMessage = message;
      }
    },

    // Shared scaffold for every mutation handler: block re-entrancy, clear
    // the banners, run the mutation and any local state patch, refetch only
    // the affected list, then report success (unless a reload set an error)
    // or the failure message.
    async runAction({
      act,
      loadingKey = 'actionLoading',
      onSuccess,
      closeDialog,
      reload = 'none',
      successMsg,
      fallbackMsg,
    }) {
      if (this[loadingKey]) return;
      try {
        this[loadingKey] = true;
        this.error = null;
        this.successMessage = null;

        await act();
        if (onSuccess) onSuccess();
        if (closeDialog) this[closeDialog] = false;
        await this.reload(reload);
        this.setSuccessIfLoaded(successMsg);
      } catch (err) {
        this.error = err.message || fallbackMsg;
      } finally {
        this[loadingKey] = false;
      }
    },

    // ==================== Invite Methods ====================
    async onCreateInvite({ emailAddress, name }) {
      await this.runAction({
        act: () => this.createUserInvite({ emailAddress, name }),
        loadingKey: 'inviteLoading',
        closeDialog: 'showInviteDialog',
        reload: 'invites',
        successMsg: `Invitation sent to ${emailAddress}`,
        fallbackMsg: 'Failed to send invitation',
      });
    },

    onDeleteInvite(invite) {
      this.selectedInvite = invite;
      this.showRevokeDialog = true;
    },

    async confirmRevoke() {
      const invite = this.selectedInvite;
      await this.runAction({
        act: () => this.deleteUserInvite(invite.id),
        // Remove locally so the list stays correct even if the reload fails.
        onSuccess: () => {
          this.invites = this.invites.filter(
            (i) => i.id !== invite.id
          );
        },
        reload: 'invites',
        successMsg: 'Invitation revoked successfully',
        fallbackMsg: 'Failed to revoke invitation',
      });
    },

    // ==================== User Management Methods ====================
    onEditUser(user) {
      this.selectedUser = user;
      this.showEditDialog = true;
    },

    // Updates the local copy of a user's status in place (preserving the
    // Researcher instance and its methods) and reassigns the array so the
    // table re-renders immediately — even if the follow-up reload fails.
    setLocalUserStatus(id, status) {
      this.users = this.users.map((u) => {
        if (u.id === id) {
          u.status = status;
        }
        return u;
      });
    },

    async onSaveUser(input) {
      await this.runAction({
        act: () => this.updateUser(input),
        loadingKey: 'editLoading',
        closeDialog: 'showEditDialog',
        reload: 'users',
        successMsg: 'User updated successfully',
        fallbackMsg: 'Failed to update user',
      });
    },

    onDeactivateUser(user) {
      this.selectedUser = user;
      this.showDeactivateDialog = true;
    },

    async confirmDeactivate() {
      const { id, name } = this.selectedUser;
      await this.runAction({
        act: () => this.deactivateUser(id),
        onSuccess: () => this.setLocalUserStatus(id, 'DEACTIVATED'),
        reload: 'users',
        successMsg: `${name} has been deactivated`,
        fallbackMsg: 'Failed to deactivate user',
      });
    },

    async onReactivateUser(user) {
      await this.runAction({
        act: () => this.reactivateUser(user.id),
        onSuccess: () => this.setLocalUserStatus(user.id, 'ACTIVE'),
        reload: 'users',
        successMsg: `${user.name} has been reactivated`,
        fallbackMsg: 'Failed to reactivate user',
      });
    },

    onDeleteUser(user) {
      this.selectedUser = user;
      this.showDeleteDialog = true;
    },

    async confirmDelete() {
      const { id, name } = this.selectedUser;
      await this.runAction({
        act: () => this.deleteUser(id),
        // Remove locally so the row disappears even if the reload fails.
        onSuccess: () => {
          this.users = this.users.filter((u) => u.id !== id);
        },
        reload: 'users',
        successMsg: `${name} has been deleted`,
        fallbackMsg: 'Failed to delete user',
      });
    },

    async onResendPasswordEmail(user) {
      await this.runAction({
        act: () => this.resendPasswordSetupEmail(user.id),
        reload: 'none',
        successMsg: `Password setup email sent to ${user.email}`,
        fallbackMsg: 'Failed to send password email',
      });
    },
  },
};
</script>

<style scoped>
.admin-content {
  max-width: 1200px;
  width: 100%;
}
</style>
